/**
 * components/brief-builder/BriefInterpretStep.tsx
 *
 * Step 2 — Show AI interpretation: extracted requirements + open questions.
 * The user can answer questions or skip if none are present.
 * Roles: Account Manager, Business Analyst
 */
import { useState } from 'react'
import type { BriefSession } from '@/types/brief-builder'

interface Props {
  session: BriefSession
  onSubmitClarifications: (answers: Record<string, string>) => void
  loading?: boolean
}

function ConfidenceBadge({ value }: { value: number }) {
  const pct  = Math.round(value * 100)
  const color = pct >= 80 ? 'text-emerald-600 bg-emerald-50 border-emerald-200'
    : pct >= 50 ? 'text-amber-600 bg-amber-50 border-amber-200'
    : 'text-red-600 bg-red-50 border-red-200'
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${color}`}>
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
        <circle cx="5" cy="5" r="4" stroke="currentColor" strokeWidth="1.3"/>
        <path d="M5 3v2.5l1.5 1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
      </svg>
      {pct}% confidence
    </span>
  )
}

export default function BriefInterpretStep({ session, onSubmitClarifications, loading }: Props) {
  const req       = session.extracted_requirements
  const questions = session.open_questions
  const [answers, setAnswers] = useState<Record<string, string>>({})

  const setAnswer = (idx: number, val: string) =>
    setAnswers(prev => ({ ...prev, [String(idx)]: val }))

  const handleSubmit = () => {
    onSubmitClarifications(answers)
  }

  if (!req) return null

  return (
    <div className="max-w-3xl mx-auto px-5 py-10 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">AI Interpretation</h2>
          <p className="text-sm text-slate-500 mt-1">
            Review what the AI extracted from the brief. Answer any open questions.
          </p>
        </div>
        <ConfidenceBadge value={session.extraction_confidence} />
      </div>

      {/* Extracted requirements */}
      <section className="bg-slate-50 rounded-xl border border-slate-100 p-5 space-y-5">
        <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
          Extracted Requirements
        </h3>

        <Grid label="Industry">{req.industry}</Grid>
        <Grid label="Markets">{req.markets.join(', ') || '—'}</Grid>
        <Grid label="Suggested Modules">
          {req.suggested_modules.map(m => (
            <span key={m} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs
                                     font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 mr-1">
              {m.replace('_', ' ')}
            </span>
          ))}
        </Grid>

        {req.stakeholders.length > 0 && (
          <div>
            <p className="text-xs font-medium text-slate-500 mb-2">Stakeholders</p>
            <div className="space-y-2">
              {req.stakeholders.map((s, i) => (
                <div key={i} className="bg-white rounded-lg border border-slate-100 p-3">
                  <p className="text-sm font-medium text-slate-800">{s.role} <span className="text-slate-400">— {s.department}</span></p>
                  {s.goals.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {s.goals.map((g, gi) => (
                        <li key={gi} className="text-xs text-slate-500 flex gap-1.5">
                          <span className="mt-1.5 shrink-0 w-1 h-1 rounded-full bg-slate-300" />
                          {g}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {req.business_goals.length > 0 && (
          <BulletList label="Business Goals" items={req.business_goals} />
        )}
        {req.use_cases.length > 0 && (
          <BulletList label="Use Cases" items={req.use_cases} />
        )}
        {req.assumptions.length > 0 && (
          <BulletList label="AI Assumptions" items={req.assumptions} accent="amber" />
        )}
        {req.missing_fields.length > 0 && (
          <BulletList label="Missing Information" items={req.missing_fields} accent="red" />
        )}
      </section>

      {/* Clarification questions */}
      {questions.length > 0 && (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">
            Open Questions
            <span className="ml-2 text-xs font-normal text-slate-400 normal-case">
              Answer these to improve recommendations
            </span>
          </h3>
          <div className="space-y-4">
            {questions.map((q, i) => (
              <div key={i} className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">
                  <span className="text-indigo-400 font-bold mr-1">Q{i + 1}.</span>{q}
                </label>
                <input
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  placeholder="Your answer (leave blank to skip)…"
                  value={answers[String(i)] ?? ''}
                  onChange={e => setAnswer(i, e.target.value)}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="flex justify-between items-center">
        <p className="text-xs text-slate-400">
          {questions.length === 0
            ? 'No open questions — the brief is complete enough to proceed.'
            : 'You can leave answers blank to let the AI make its best guess.'}
        </p>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="px-6 py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold
                     hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed
                     transition-colors"
        >
          {loading ? 'Generating recommendations…' : 'Generate Recommendations →'}
        </button>
      </div>
    </div>
  )
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function Grid({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3">
      <p className="text-xs font-medium text-slate-500 w-32 shrink-0 pt-0.5">{label}</p>
      <div className="text-sm text-slate-800">{children}</div>
    </div>
  )
}

function BulletList({ label, items, accent }: {
  label: string
  items: string[]
  accent?: 'amber' | 'red'
}) {
  const color = accent === 'amber' ? 'text-amber-600' : accent === 'red' ? 'text-red-500' : 'text-slate-400'
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="flex gap-2 text-xs text-slate-600">
            <span className={`mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full ${
              accent === 'amber' ? 'bg-amber-400' : accent === 'red' ? 'bg-red-400' : 'bg-slate-300'
            }`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}
